import { createContext, useContext, useReducer, useCallback, useEffect, useRef, ReactNode } from 'react'
import { ConnectionStatus } from '@/types/serial'
import { useToast } from '@/contexts/ToastContext'

interface ConnectionContextValue {
  isConnected: boolean
  status: ConnectionStatus
  error: string | null
  port: SerialPort | null
  connect: () => Promise<void>
  disconnect: () => Promise<void>
  sendCommand: (command: string, timeout?: number) => Promise<string>
  onMessage: (callback: (message: string) => void) => () => void
  downloadBinaryFile: (filename: string, onProgress?: (progress: number) => void) => Promise<ArrayBuffer>
}

const ConnectionContext = createContext<ConnectionContextValue | null>(null)

export function useConnection() {
  const context = useContext(ConnectionContext)
  if (!context) {
    throw new Error('useConnection must be used within a ConnectionProvider')
  }
  return context
}

type ConnectionAction =
  | { type: 'CONNECTING' }
  | { type: 'CONNECTED'; port: SerialPort }
  | { type: 'DISCONNECTED' }
  | { type: 'ERROR'; error: string }

interface SimpleSerialState {
  port: SerialPort | null
  status: ConnectionStatus
  error: string | null
}

function connectionReducer(state: SimpleSerialState, action: ConnectionAction): SimpleSerialState {
  switch (action.type) {
    case 'CONNECTING':
      return { ...state, status: 'connecting', error: null }
    case 'CONNECTED':
      return { status: 'connected', port: action.port, error: null }
    case 'DISCONNECTED':
      return { port: null, status: 'disconnected', error: null }
    case 'ERROR':
      return { ...state, status: 'error', error: action.error }
    default:
      return state
  }
}

const initialState: SimpleSerialState = {
  port: null,
  status: 'disconnected',
  error: null,
}

interface ConnectionProviderProps {
  children: ReactNode
}

export function ConnectionProvider({ children }: ConnectionProviderProps) {
  const [state, dispatch] = useReducer(connectionReducer, initialState)
  const { addToast } = useToast()
  const messageCallbacks = useRef<Set<(message: string) => void>>(new Set())
  const readLoopActive = useRef(false)
  const autoConnectAttempted = useRef(false)
  const textEncoder = useRef(new TextEncoder())
  const textDecoder = useRef(new TextDecoder())
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null)

  // Helper to open a port and set up streams
  const openPort = useCallback(async (port: SerialPort, showToast = true) => {
    await port.open({ baudRate: 115200 })

    // Wait for ESP32 to finish booting (it resets when serial port opens)
    console.log('[Serial] Waiting for ESP32 to boot...')
    await new Promise(resolve => setTimeout(resolve, 2000))

    // Get reader directly from port
    const reader = port.readable!.getReader()
    readerRef.current = reader

    dispatch({ type: 'CONNECTED', port })

    // Start reading loop
    readLoop(reader, port)

    if (showToast) {
      addToast({
        type: 'success',
        title: 'Connected',
        description: 'Successfully connected to ESP32',
      })
    }
  }, [addToast])

  // Try to auto-connect to previously authorized port
  const tryAutoConnect = useCallback(async () => {
    if (autoConnectAttempted.current) return
    autoConnectAttempted.current = true

    if (!navigator.serial) return

    try {
      const ports = await navigator.serial.getPorts()
      if (ports.length > 0) {
        const port = ports[0]
        dispatch({ type: 'CONNECTING' })
        await openPort(port, false)
        addToast({
          type: 'success',
          title: 'Auto-connected',
          description: 'Reconnected to previously authorized device',
        })
      }
    } catch (error) {
      console.log('Auto-connect not available:', error)
    }
  }, [openPort, addToast])

  // Manual connect - prompts user to select port
  const connect = useCallback(async () => {
    try {
      dispatch({ type: 'CONNECTING' })

      if (!navigator.serial) {
        throw new Error('WebSerial API not supported. Please use Chrome or Edge.')
      }

      const port = await navigator.serial.requestPort()
      await openPort(port)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to connect'
      if (message.includes('No port selected')) {
        dispatch({ type: 'DISCONNECTED' })
        return
      }
      dispatch({ type: 'ERROR', error: message })
      addToast({
        type: 'error',
        title: 'Connection failed',
        description: message,
      })
    }
  }, [openPort, addToast])

  const readLoop = async (reader: ReadableStreamDefaultReader<Uint8Array>, port: SerialPort, reconnectAttempts = 0) => {
    if (readLoopActive.current) {
      console.log('[Serial] Read loop already active, skipping')
      return
    }
    readLoopActive.current = true
    console.log('[Serial] Read loop started')

    let buffer = ''
    let hasReceivedData = false

    try {
      while (true) {
        const { value, done } = await reader.read()
        if (done) {
          console.log('[Serial] Reader done signal received')
          // ESP32 may have reset - try to get a new reader if port is still open
          // Only retry if we haven't exceeded attempts and port is still open
          if (port.readable && reconnectAttempts < 3) {
            console.log(`[Serial] Reconnecting reader after reset (attempt ${reconnectAttempts + 1}/3)...`)
            reader.releaseLock()
            // Wait a bit for ESP32 to stabilize after reset
            await new Promise(resolve => setTimeout(resolve, 500))
            if (port.readable) {
              const newReader = port.readable.getReader()
              readerRef.current = newReader
              readLoopActive.current = false
              readLoop(newReader, port, hasReceivedData ? 0 : reconnectAttempts + 1)
              return
            }
          }
          break
        }
        if (value) {
          hasReceivedData = true
          // Decode bytes to string
          const text = textDecoder.current.decode(value, { stream: true })
          buffer += text
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''
          for (const line of lines) {
            const trimmed = line.trim()
            if (trimmed) {
              console.log('[Serial] RX:', trimmed)
              messageCallbacks.current.forEach(cb => cb(trimmed))
            }
          }
        }
      }
    } catch (error) {
      console.log('[Serial] Read loop error:', error)
    } finally {
      console.log('[Serial] Read loop ended')
      readLoopActive.current = false
    }
  }

  const disconnect = useCallback(async () => {
    try {
      // Cancel the reader if it exists
      if (readerRef.current) {
        try {
          await readerRef.current.cancel()
        } catch {
          // Reader may already be closed
        }
        readerRef.current = null
      }

      // Reset read loop flag so new connection can start fresh
      readLoopActive.current = false

      // Reset text decoder for next connection
      textDecoder.current = new TextDecoder()

      // Small delay to let streams settle
      await new Promise(resolve => setTimeout(resolve, 100))

      // Now close the port
      if (state.port) {
        await state.port.close()
      }

      // Reset auto-connect flag so reconnection works
      autoConnectAttempted.current = false

      dispatch({ type: 'DISCONNECTED' })
      addToast({
        type: 'info',
        title: 'Disconnected',
        description: 'Connection closed',
      })
    } catch (error) {
      console.error('Disconnect error:', error)
      // Still reset state even on error
      readerRef.current = null
      readLoopActive.current = false
      autoConnectAttempted.current = false
      dispatch({ type: 'DISCONNECTED' })
    }
  }, [state.port, addToast])

  // Send a text command and wait for OK/ERROR response
  const sendCommand = useCallback(async (command: string, timeout = 10000): Promise<string> => {
    if (!state.port || state.status !== 'connected') {
      throw new Error('Not connected')
    }

    // Get a writer, send command, release writer
    const writer = state.port.writable!.getWriter()
    try {
      const encoded = textEncoder.current.encode(command + '\n')
      await writer.write(encoded)
    } finally {
      writer.releaseLock()
    }

    // Wait for response
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        cleanup()
        reject(new Error(`Command timeout: ${command}`))
      }, timeout)

      const cleanup = () => {
        clearTimeout(timeoutId)
        messageCallbacks.current.delete(handleResponse)
      }

      const handleResponse = (message: string) => {
        if (message.startsWith('OK:') || message.startsWith('ERROR:')) {
          cleanup()
          if (message.startsWith('ERROR:')) {
            reject(new Error(message.substring(6)))
          } else {
            resolve(message)
          }
        }
      }

      messageCallbacks.current.add(handleResponse)
    })
  }, [state.port, state.status])

  const onMessage = useCallback((callback: (message: string) => void): (() => void) => {
    messageCallbacks.current.add(callback)
    return () => {
      messageCallbacks.current.delete(callback)
    }
  }, [])

  // Download a binary file from the device
  // This temporarily takes over the serial reader for raw binary transfer
  const downloadBinaryFile = useCallback(async (
    filename: string,
    onProgress?: (progress: number) => void
  ): Promise<ArrayBuffer> => {
    if (!state.port || state.status !== 'connected') {
      throw new Error('Not connected')
    }

    const port = state.port

    // Step 1: Send DOWNLOAD command
    const writer = port.writable!.getWriter()
    try {
      const cmd = textEncoder.current.encode(`DOWNLOAD ${filename}\n`)
      await writer.write(cmd)
    } finally {
      writer.releaseLock()
    }

    // Step 2: Wait for OK:DOWNLOAD:filename:size header via message callback
    const headerInfo = await new Promise<{ filename: string; size: number }>((resolve, reject) => {
      const timeout = setTimeout(() => {
        cleanup()
        reject(new Error('Download header timeout'))
      }, 10000)

      const cleanup = () => {
        clearTimeout(timeout)
        messageCallbacks.current.delete(handleMessage)
      }

      const handleMessage = (message: string) => {
        if (message.startsWith('OK:DOWNLOAD:')) {
          const parts = message.split(':')
          if (parts.length >= 4) {
            cleanup()
            resolve({
              filename: parts[2],
              size: parseInt(parts[3], 10)
            })
          }
        } else if (message.startsWith('ERROR:')) {
          cleanup()
          reject(new Error(message.substring(6)))
        }
      }

      messageCallbacks.current.add(handleMessage)
    })

    console.log(`[Download] Starting download: ${headerInfo.filename} (${headerInfo.size} bytes)`)
    onProgress?.(0)

    // Step 3: Cancel current reader to stop text processing loop
    if (readerRef.current) {
      try {
        await readerRef.current.cancel()
      } catch {
        // Reader may already be cancelled
      }
      readerRef.current = null
    }

    // Wait for read loop to fully stop
    await new Promise(resolve => setTimeout(resolve, 50))

    // Step 4: Get a new reader for binary data
    const binaryReader = port.readable!.getReader()
    const fileBuffer = new Uint8Array(headerInfo.size)
    let bytesReceived = 0

    try {
      // Send READY to start transfer
      const readyWriter = port.writable!.getWriter()
      try {
        await readyWriter.write(textEncoder.current.encode('READY\n'))
      } finally {
        readyWriter.releaseLock()
      }

      // Step 5: Read binary data until we have all bytes
      while (bytesReceived < headerInfo.size) {
        const { value, done } = await binaryReader.read()
        if (done) break
        if (value) {
          // Copy received bytes to file buffer
          const bytesToCopy = Math.min(value.length, headerInfo.size - bytesReceived)
          fileBuffer.set(value.subarray(0, bytesToCopy), bytesReceived)
          bytesReceived += bytesToCopy

          // Update progress
          const progress = Math.floor((bytesReceived / headerInfo.size) * 100)
          onProgress?.(progress)
        }
      }

      console.log(`[Download] Received ${bytesReceived}/${headerInfo.size} bytes`)

      // Step 6: Read and discard END_DOWNLOAD marker and any trailing data
      // Give a small timeout to read any trailing messages
      const endMarkerTimeout = setTimeout(() => {}, 500)
      try {
        const { value } = await Promise.race([
          binaryReader.read(),
          new Promise<{ value: undefined; done: true }>((resolve) =>
            setTimeout(() => resolve({ value: undefined, done: true }), 200)
          )
        ])
        if (value) {
          const text = textDecoder.current.decode(value)
          console.log('[Download] Trailing data:', text.trim())
        }
      } catch {
        // Ignore timeout
      }
      clearTimeout(endMarkerTimeout)

    } finally {
      // Step 7: Release binary reader and restart text processing loop
      binaryReader.releaseLock()

      // Small delay before restarting
      await new Promise(resolve => setTimeout(resolve, 100))

      // Restart the text read loop
      if (port.readable) {
        const newReader = port.readable.getReader()
        readerRef.current = newReader
        readLoopActive.current = false
        readLoop(newReader, port)
      }
    }

    if (bytesReceived !== headerInfo.size) {
      throw new Error(`Incomplete download: got ${bytesReceived}/${headerInfo.size} bytes`)
    }

    return fileBuffer.buffer
  }, [state.port, state.status])

  // Try auto-connect on mount
  useEffect(() => {
    tryAutoConnect()
  }, [tryAutoConnect])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cancel reader and close port on unmount
      if (readerRef.current) {
        readerRef.current.cancel().catch(() => {})
      }
      if (state.port) {
        state.port.close().catch(() => {})
      }
    }
  }, [state.port])

  const value: ConnectionContextValue = {
    isConnected: state.status === 'connected',
    status: state.status,
    error: state.error,
    port: state.port,
    connect,
    disconnect,
    sendCommand,
    onMessage,
    downloadBinaryFile,
  }

  return (
    <ConnectionContext.Provider value={value}>
      {children}
    </ConnectionContext.Provider>
  )
}
